import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toJpeg } from 'html-to-image';
import { jsPDF } from 'jspdf';

interface PreviewProps {
  nurseId: number;
  onClose: () => void;
}

const ExperienceCertificatePreview: React.FC<PreviewProps> = ({ nurseId, onClose }) => {
  const [nurse, setNurse] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showLetterhead, setShowLetterhead] = useState(false);
  const [issueDate, setIssueDate] = useState(new Date().toISOString().split('T')[0]);
  const [tillPresent, setTillPresent] = useState(false);
  const [idCardFrontUrl, setIdCardFrontUrl] = useState<string | null>(null);
  const certificateRef = useRef<HTMLDivElement>(null);

  // 1. EXPANDED STATE to hold the boilerplate text blocks
  const [editableData, setEditableData] = useState({
    full_name: '',
    father_spouse_name: '',
    dob: '',
    sex: 'female',
    emp_id: '',
    passport_number: '',
    council_name: 'Karnataka State Nursing Council',
    registration_number: '',
    exp_start_date: '',
    exp_end_date: '',
    designation: '',
    dept: '',
    hours_per_week: '48',
    gender: 'female',
    
    // Boilerplate Blocks
    ref_no_text: '',
    cert_title: 'TO WHOMSOEVER IT MAY CONCERN',
    paragraph_one: '',
    paragraph_two: '',
    company_name: 'For LHHS Healthcare Pvt Ltd',
    sign_name: 'Leo Devasia',
    sign_title: 'Director - Nursing ',
    sign_mobile: 'Mobile No - +91 99166 74130',
    sign_contact: 'Contact - contact@lhhs.com'
  });

  const API_BASE_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";

  useEffect(() => {
    const fetchNurseDetails = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        const response = await axios.get(`${API_BASE_URL}/api/nurses/${nurseId}`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        const d = response.data;
        setNurse(d);
        
        if (d.id_card_front_url) {
          setIdCardFrontUrl(d.id_card_front_url.startsWith('http') ? d.id_card_front_url : `${API_BASE_URL}/${d.id_card_front_url}`);
        }
        
        // Calculate pronouns for the initial 2nd paragraph generation
        const gen = d.sex?.toLowerCase() === 'male' ? 'male' : 'female';
        const subj = gen === 'male' ? 'he' : 'she';
        const poss = gen === 'male' ? 'his' : 'her';
        const obj = gen === 'male' ? 'him' : 'her';

        const rawReg = d.registrations?.[0]?.register_number || d.exp_nursing_reg_txt || '';
        let extractedCouncil = d.registrations?.[0]?.council_name || 'Karnataka State Nursing Council';
        let extractedNumber = d.registrations?.[0]?.register_number || '';

        if (!d.registrations?.length && d.exp_nursing_reg_txt) {
            if (d.exp_nursing_reg_txt.includes('-')) {
                const parts = d.exp_nursing_reg_txt.split('-');
                extractedCouncil = parts[0].trim();
                extractedNumber = parts.slice(1).join('-').trim();
            } else {
                extractedNumber = d.exp_nursing_reg_txt;
            }
        }

        setEditableData({
          full_name: d.full_name || '',
          father_spouse_name: d.father_spouse_name || '',
          dob: d.dob || '',
          sex: d.sex || 'female',
          emp_id: d.emp_id || '',
          passport_number: d.passport_number || 'N/A',
          council_name: extractedCouncil,
          registration_number: extractedNumber,
          exp_start_date: d.exp_start_date || d.date_of_joining || '',
          exp_end_date: d.exp_end_date || new Date().toISOString().split('T')[0],
          designation: d.exp_designation || d.designation || 'Registered Nurse',
          dept: d.exp_department || '', 
          hours_per_week: d.hours_per_week || '48',
          gender: gen,

                // Initialize Boilerplate Blocks
          ref_no_text: `HR Ref. No: ${d.id ? d.id + 5000 : ''}`,
          cert_title: 'TO WHOMSOEVER IT MAY CONCERN',
          paragraph_one: d.exp_paragraph_one || (() => {
            const rel = gen === 'male' ? 'S/o' : 'D/o';
            const dobFmt   = formatToOrdinalDate(d.dob || '');
            const startFmt = formatToOrdinalDate(d.exp_start_date || d.date_of_joining || '');
            const endFmt   = formatToOrdinalDate(d.exp_end_date || new Date().toISOString().split('T')[0]);
            const desig    = d.exp_designation || d.designation || 'Registered Nurse';
            const dept     = d.exp_department || '';
            const hrs      = d.hours_per_week || '48';
            const passport = d.passport_number || 'N/A';
            let p1 = `This is to certify that ${d.full_name || ''}, ${rel} ${d.father_spouse_name || ''}, Date of Birth ${dobFmt}, Employee Number : ${d.emp_id || ''}`;
            if (extractedNumber || extractedCouncil) {
              p1 += `, with ${extractedCouncil} Registration Number : ${extractedNumber}`;
            }
            p1 += `, Passport Number : ${passport}, was working in our organization as a full-time ${desig}, in ${dept}, ${hrs} hours per week, from ${startFmt} to ${endFmt}.`;
            return p1;
          })(),
          paragraph_two: d.exp_paragraph_two || `Moreover, I would like to reflect over ${poss} conduct during ${poss} stay with us. During ${poss} service, ${subj} has been found sincere, reliable, trustworthy, sociable, pleasant and open to challenges. ${subj} has a genial temperament and can efficiently work in a team. All of our staff members are pleased with ${obj} and feels comfortable in teaming and coordinating with ${obj} for the realization of organizational goals and objectives.`,
          company_name: 'For LHHS Healthcare Pvt Ltd',
          sign_name: 'Leo Devasia',
          sign_title: 'Director - Nursing',
          sign_mobile: 'Mobile No - +91 99166 74130',
          sign_contact: 'Contact - contact@lhhs.com'
        });
      } catch (error) { console.error(error); } finally { setLoading(false); }
    };
    fetchNurseDetails();
  }, [nurseId, API_BASE_URL]);

  // Helper to get 'st', 'nd', 'rd', or 'th'
  const getOrdinalSuffix = (day: number) => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return "st";
      case 2: return "nd";
      case 3: return "rd";
      default: return "th";
    }
  };

  // New Date Formatter
  const formatToOrdinalDate = (dateString: string) => {
    if (!dateString) return '';
    
    let year, monthIdx, day;
    const parts = dateString.split('-');
    
    if (parts.length === 3) {
      // Handles YYYY-MM-DD from the database/inputs
      if (parts[0].length === 4) {
        year = Number(parts[0]);
        monthIdx = Number(parts[1]) - 1;
        day = Number(parts[2]);
      } 
      // Handles DD-MM-YYYY just in case
      else if (parts[2].length === 4) {
        year = Number(parts[2]);
        monthIdx = Number(parts[1]) - 1;
        day = Number(parts[0]);
      } else {
        return dateString;
      }
    } else {
      return dateString;
    }

    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    const monthName = monthNames[monthIdx];
    
    if (!monthName || isNaN(day) || isNaN(year)) return dateString;

    return `${day}${getOrdinalSuffix(day)} ${monthName}, ${year}`;
  };

  const handleSave = async () => {
    setIsSaving(true);
    
    if (certificateRef.current) {
        certificateRef.current.classList.add('hide-borders');
    }

    try {
      const token = localStorage.getItem('accessToken');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

      const patchData = {
        full_name: editableData.full_name,
        dob: editableData.dob,
        sex: editableData.sex,
        father_spouse_name: editableData.father_spouse_name,
        experience_certificate_issued: true,
        exp_start_date: editableData.exp_start_date,
        exp_end_date: editableData.exp_end_date,
        exp_designation: editableData.designation,
        exp_department: editableData.dept,
        hours_per_week: editableData.hours_per_week,
        exp_nursing_reg_txt: `${editableData.council_name} - ${editableData.registration_number}`,
        exp_paragraph_one: editableData.paragraph_one,
        exp_paragraph_two: editableData.paragraph_two,
      };

      const formDataPatch = new FormData();
      formDataPatch.append('nurse_data', JSON.stringify(patchData));

      await axios.patch(`${API_BASE_URL}/api/nurses/${nurseId}`, formDataPatch, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      
      if (certificateRef.current) {
        const dataUrl = await toJpeg(certificateRef.current, { 
            quality: 1.0, 
            pixelRatio: 2,
            backgroundColor: '#ffffff'
        });

        const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
        pdf.addImage(dataUrl, 'JPEG', 0, 0, 210, 297);
        const pdfBlob = pdf.output('blob');
        const file = new File([pdfBlob], 'experience_certificate.pdf', { type: 'application/pdf' });

        const formDataFile = new FormData();
        formDataFile.append('certificate_file', file);

        await axios.post(`${API_BASE_URL}/api/nurses/${nurseId}/save-experience-certificate`, formDataFile, {
          headers: { ...headers, 'Content-Type': 'multipart/form-data' }
        });
      }

      alert("Certificate successfully saved!");
    } catch (error) { 
      console.error("Save failed", error);
      alert("Failed to save the certificate.");
    } finally { 
        if (certificateRef.current) {
            certificateRef.current.classList.remove('hide-borders');
        }
        setIsSaving(false); 
    }
  };

  const handlePrint = () => {
    window.print();
  };

  // BLOCK TEXT (For headers, paragraphs, and signatures)
  const EditableBlock = ({ name, value, tag: Tag = 'div', style = {} }: any) => (
    <Tag
      contentEditable
      suppressContentEditableWarning={true}
      onBlur={(e: any) => {
        const newText = e.currentTarget.innerText || '';
        setEditableData(prev => ({ ...prev, [name]: newText }));
      }}
      className="editable-document"
      style={style}
    >
      {value}
    </Tag>
  );

  if (loading) return <div style={{ color: 'white', padding: '2rem' }}>Loading...</div>;

  return (
    <div className="main-preview-wrapper" style={{ display: 'flex', gap: '20px', padding: '20px', height: '90vh', width: '100%', maxWidth: '1200px' }}>
      
      <div className="no-print" style={sidebarStyle}>
        <h3 style={{ marginTop: 0 }}>Certificate Detail</h3>
        
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginBottom: '15px' }}>
          <input type="checkbox" checked={showLetterhead} onChange={(e) => setShowLetterhead(e.target.checked)} />
          Print Letterhead
        </label>

        <label style={labelStyle}>Gender Pronouns</label>
        <select style={inputStyle} value={editableData.gender} onChange={e => setEditableData({...editableData, gender: e.target.value})}>
            <option value="female">Female (She/Her)</option>
            <option value="male">Male (He/Him)</option>
        </select>

        <label style={labelStyle}>Issue Date</label>
        <input type="date" style={inputStyle} value={issueDate} onChange={e => setIssueDate(e.target.value)} />

        <label style={labelStyle}>Start Date</label>
        <input type="date" style={inputStyle} value={editableData.exp_start_date} onChange={e => setEditableData({...editableData, exp_start_date: e.target.value})} />

        <label style={labelStyle}>End Date</label>
        <input
          type="date"
          style={{ ...inputStyle, opacity: tillPresent ? 0.4 : 1, pointerEvents: tillPresent ? 'none' : 'auto' }}
          value={editableData.exp_end_date}
          onChange={e => setEditableData({...editableData, exp_end_date: e.target.value})}
          disabled={tillPresent}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '6px', cursor: 'pointer', fontSize: '12px', color: '#475569' }}>
          <input type="checkbox" checked={tillPresent} onChange={e => setTillPresent(e.target.checked)} />
          Till Present
        </label>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '20px' }}>
          <button onClick={handleSave} disabled={isSaving} style={saveButtonStyle}>
            {isSaving ? '⏳ Saving...' : '💾 Save to System'}
          </button>
          <button onClick={handlePrint} style={printButtonStyle}>🖨️ Print</button>
          <button onClick={onClose} style={closeButtonStyle}>Close</button>
        </div>
      </div>

      <div className="preview-container" style={scrollablePreviewContainerStyle}>
        <div id="printable-area">
          <div ref={certificateRef} className="a4-page" style={{ ...pageStyle, backgroundImage: showLetterhead ? 'url("/assets/letterhead.png")' : 'none', backgroundSize: '100% 100%' }}>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '25px', fontSize: '16px' }}>
              <EditableBlock name="ref_no_text" value={editableData.ref_no_text} />
              <div>Date: {formatToOrdinalDate(issueDate)}</div>
            </div>

            <EditableBlock 
              name="cert_title" 
              value={editableData.cert_title} 
              tag="h3" 
              style={{ textAlign: 'center', textDecoration: 'underline', marginBottom: '25px', fontSize: '18px' }} 
            />

            <div style={{ textAlign: 'justify', lineHeight: '1.9', fontSize: '16px' }}>
              <EditableBlock
                name="paragraph_one"
                value={editableData.paragraph_one}
                tag="p"
                style={{ margin: 0 }}
              />
              
              {!tillPresent && (
                <EditableBlock
                  name="paragraph_two"
                  value={editableData.paragraph_two}
                  tag="p"
                  style={{ marginTop: '20px', fontStyle: 'italic' }}
                />
              )}
            </div>

            {/* Signature Block */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '40px', marginTop: '30px' }}>
              <div style={{ minWidth: '250px' }}>
                <EditableBlock name="company_name" value={editableData.company_name} tag="p" style={{ fontWeight: 'bold', margin: 0 }} />
                
                <div style={{ position: 'relative', height: '140px', marginTop: '10px' }}>
                   <img crossOrigin="anonymous" src="/assets/sign.png" alt="Sign" style={{ position: 'absolute', left: '-20px', top: '0px', height: '140px', mixBlendMode: 'multiply' }} />
                   <img crossOrigin="anonymous" src="/assets/stamp.png" alt="Stamp" style={{ position: 'absolute', left: '110px', top: '5px', width: '130px', opacity: 0.85, mixBlendMode: 'multiply' }} />
                </div>
                
                <EditableBlock name="sign_name" value={editableData.sign_name} tag="p" style={{ margin: 0, fontWeight: 'bold' }} />
                <EditableBlock name="sign_title" value={editableData.sign_title} tag="p" style={{ margin: 0 }} />
                <EditableBlock name="sign_mobile" value={editableData.sign_mobile} tag="p" style={{ margin: 0, marginTop: '8px' }} />
                <EditableBlock name="sign_contact" value={editableData.sign_contact} tag="p" style={{ margin: 0 }} />
              </div>
              
              <div>
                {idCardFrontUrl && (
                    <img crossOrigin="anonymous" src={`${idCardFrontUrl}?t=${new Date().getTime()}`} alt="ID Card" style={{ width: '2.125in', height: '3.375in', border: '1px solid #eee' }} />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        /* Inline Blanks */
        .editable-field {
          border-bottom: 1px solid #ccc;
          min-width: 80px; 
          min-height: 1.2em; 
          display: inline-block;
          cursor: text;
          padding: 0 4px;
          transition: all 0.2s;
          vertical-align: bottom;
        }

        .editable-field:focus {
          background-color: #f1f5f9;
          outline: none;
          border-bottom: 2px solid #2563eb;
        }

        /* Block Text (Headers, Paragraphs) */
        .editable-document {
          outline: none;
          border-radius: 4px;
          border: 1px dashed transparent;
          transition: all 0.2s ease;
          cursor: text;
        }

        .editable-document:focus, .editable-document:hover {
          border-color: #94a3b8;
          background-color: rgba(241, 245, 249, 0.4);
        }

        /* HIDE ALL EDITING UI IN PDF AND PRINT */
        .hide-borders .editable-field {
          border-bottom: none !important;
          padding: 0 !important;
          min-width: auto !important;
        }
        .hide-borders .editable-document {
          border-color: transparent !important;
          background-color: transparent !important;
        }

        /* STRICT ONE PAGE PRINT CSS */
        @media print {
          @page { size: A4; margin: 0; }
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            width: 210mm !important;
            height: 297mm !important;
            overflow: hidden !important;
            background-color: white !important;
            -webkit-print-color-adjust: exact !important; 
            print-color-adjust: exact !important;         
          }
          .no-print { display: none !important; }
          #printable-area {
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            width: 210mm !important;
            height: 297mm !important;
            margin: 0 !important;
            padding: 0 !important;
            z-index: 9999;
          }
          .a4-page {
            box-shadow: none !important;
            border: none !important;
            margin: 0 !important;
            -webkit-print-color-adjust: exact !important; 
            print-color-adjust: exact !important;         
          }
          .editable-field { border-bottom: none !important; padding: 0 !important; min-width: auto !important; }
          .editable-document { border-color: transparent !important; background-color: transparent !important; }
        }
      `}</style>
    </div>
  );
};

// Styling Constants
const labelStyle = { display: 'block', fontSize: '11px', fontWeight: 700, color: '#64748b', marginTop: '12px' };
const inputStyle = { width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1', marginTop: '4px' };
const sidebarStyle = { backgroundColor: '#f8fafc', padding: '20px', borderRadius: '12px', width: '320px', height: '100%', overflowY: 'auto' as const };
const saveButtonStyle = { width: '100%', padding: '12px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 700, cursor: 'pointer' };
const printButtonStyle = { width: '100%', padding: '12px', backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 700, cursor: 'pointer' };
const closeButtonStyle = { width: '100%', padding: '10px', background: 'white', color: '#475569', border: '1px solid #cbd5e1', borderRadius: '6px' };
const scrollablePreviewContainerStyle = { flex: 1, backgroundColor: '#cbd5e1', padding: '20px', borderRadius: '12px', overflowY: 'auto' as const, display: 'flex', justifyContent: 'center' };
const pageStyle = { backgroundColor: 'white', width: '210mm', height: '297mm', padding: '45mm 25.4mm 30mm', boxSizing: 'border-box' as const, fontFamily: '"Times New Roman", Times, serif', boxShadow: '0 10px 25px rgba(0,0,0,0.1)', overflow: 'hidden' };

export default ExperienceCertificatePreview;